// @flow
import ResourceModel from "../base/ResourceModel";
import ResourceCollection from "../base/ResourceCollection";
import FilterCollection from "../filters/FilterCollection";
import ConceptIndexFilterModel from "../filters/ConceptIndexFilterModel";
import FilterModel from "../filters/FilterModel";
import ConceptLinkModel from "./ConceptLinkModel";

import { TIMEVERSION_FILTER_NAME } from "../../constants/Constants";

import type { ModularUIResponse } from "../../modularui";
import type { ModularUIModel } from "../types";
import type LinkModel from "../links/LinkModel";
import type Href from "../href/Href";
import type ErrorResponse from "../error/ErrorResponse";

/**
 * Get Index of concepts, to filter model catalog
 */
export default class ConceptIndexModel extends ResourceModel {
  _filterCollection: FilterCollection;
  _concepts: ResourceCollection<ConceptLinkModel>;

  /**
   */
  constructor(modularuiResponse: ModularUIResponse) {
    super(modularuiResponse);

    this._filterCollection = new FilterCollection(
      this.data.filter,
      {
        filter: this.contributions.filter,
        dynamicschema: this.data.dynamicschema,
      },
      this.modelOptions,
    );

    this._concepts = new ResourceCollection();
    this._concepts.collection = this.data._embedded
      ? this.data._embedded.results.map(
          (concept) =>
            new ConceptLinkModel(
              concept.concept,
              this.entryDate,
              this.modelOptions,
            ),
        )
      : [];
  }

  /**
   */
  get type(): string {
    if (this.resourcetype === "RelatedConcepts") {
      return "RelatedConcepts";
    }

    return "ConceptIndex";
  }

  /**
   */
  static get modelName(): string {
    return "ConceptIndexModel";
  }

  /**
   */
  static isApplicableModel(data: ModularUIResponse): boolean {
    const resourceType = data.contributions?.resourcetype;
    return (
      resourceType === "ConceptSearch" ||
      resourceType === "relatedConcepts" ||
      resourceType === "RelatedConcepts"
    );
  }

  /**
   */
  getInitialChildModelLinks(): Array<LinkModel> {
    return this.items.getInitialChildModelLinks();
  }

  /**
   */
  setChildModels(models: Array<ModularUIModel>, errors: Array<ErrorResponse>) {
    this.items.setChildModels(models, errors);
  }

  /**
   * Getting the self link of this list
   */
  get selfhref(): Href {
    const { href } = this.selflink;

    if (this.filterCollection.hasItems) {
      this.filterCollection.forEach((filter) => {
        filter.params.forEach((param) => {
          if (param.value) {
            href.setParameter(param.name, param.value);
          } else {
            href.removeParameter(param.name);
          }
        });
      });
    }

    return href;
  }

  /**
   */
  get label(): string {
    return this.getContribution("label");
  }

  /**
   * Retrieve filters of conceptindex model
   */
  get filterCollection(): FilterCollection {
    return this._filterCollection;
  }

  /**
   * Get index filter
   */
  get indexfilter(): ConceptIndexFilterModel | null {
    const indexFilter = this.filterCollection.getFilterByAttributeKey("index");
    if (indexFilter instanceof ConceptIndexFilterModel) {
      return indexFilter;
    }
    return null;
  }

  /**
   * Retrieve modelcatalog.js of content toc
   */
  get entryDate(): ISO_DATE | null {
    const timeversionFilter = this.filterCollection.getFilterByAttributeKey(
      TIMEVERSION_FILTER_NAME,
    );

    const date = timeversionFilter?.value;
    if (typeof date === "string") {
      return date;
    }
    return null;
  }

  /**
   * get searchterm filter
   */
  get searchtermfilter(): FilterModel | null {
    const termFilter = this.filterCollection.getFilterByAttributeKey("label");
    if (termFilter instanceof FilterModel) {
      return termFilter;
    }
    return null;
  }

  /**
   * Get model category filter
   */
  get modelCategoryFilter(): FilterModel | null {
    const categoryFilter =
      this.filterCollection.getFilterByAttributeKey("modelCategory");
    if (categoryFilter instanceof FilterModel) {
      return categoryFilter;
    }
    return null;
  }

  /**
   * Get concept links found by index filter
   */
  get items(): ResourceCollection<ConceptLinkModel> {
    return this._concepts;
  }

  /**
   * Replace the items collection
   */
  set items(itemCollection: ResourceCollection<ConceptLinkModel>) {
    this._concepts = itemCollection;
  }
}
